import axios from '@/api/provider';

/**
 * Fetch available documents (filings and transcripts) for a company
 * Form types and date range are read from dashboard settings on the backend.
 * @param {Object} params - The fetch parameters
 * @param {string} params.companyName - Company name
 * @param {string} params.companyIdentifier - Company ticker or CIK
 * @param {string} params.companyRegion - Company region ('us' or 'non-us')
 * @param {boolean} params.higherEffortSearch - Use higher effort AI search for non-US companies
 * @returns {Promise} Promise with document data and settings used
 */
const fetchDocuments = async ({ companyName, companyIdentifier, companyRegion, higherEffortSearch = false, exchange = null, exchangeSlug = null, companyNumber = null, subsidiaryNumbers = null, supplementWithWebSearch = false, fromYear = null, toYear = null, formTypes = null, uen = null, sgxCode = null }) => {
  // All paths are async now (POST returns immediately), but keep a generous
  // timeout as a fallback in case the backend responds synchronously
  const timeoutMs = 5 * 60 * 1000;

  const payload = {
    company_name: companyName,
    company_region: companyRegion,
    higher_effort_search: higherEffortSearch
  };
  if (companyIdentifier) payload.ticker_or_cik = companyIdentifier;
  if (exchange) payload.exchange = exchange;
  if (exchangeSlug) payload.exchange_slug = exchangeSlug;
  if (uen) payload.uen = uen;
  if (sgxCode) payload.sgx_code = sgxCode;
  if (companyNumber) payload.company_number = companyNumber;
  if (subsidiaryNumbers?.length) payload.subsidiary_numbers = subsidiaryNumbers;
  if (supplementWithWebSearch) payload.supplement_with_web_search = true;
  if (fromYear) payload.from_year = fromYear;
  if (toYear) payload.to_year = toYear;
  if (formTypes?.length) payload.form_types = formTypes;

  return axios
    .post('onboarding/fetch-documents', payload, { timeout: timeoutMs })
    .then((response) => ({
      success: response.success,
      message: response.message,
      asyncMode: response.async_mode || false,
      taskId: response.task_id || null,
      companyName: response.company_name,
      totalFilings: response.total_filings,
      totalTranscripts: response.total_transcripts,
      unprocessedFilings: response.unprocessed_filings,
      unprocessedTranscripts: response.unprocessed_transcripts,
      processedFilings: response.processed_filings || [],
      processedTranscripts: response.processed_transcripts || [],
      filings: response.filings || [],
      transcripts: response.transcripts || [],
      settingsUsed: response.settings_used || null
    }))
    .catch((error) => {
      console.error('Fetch documents error:', error);
      throw error;
    });
};

/**
 * Process selected documents for onboarding
 * @param {Object} params - The process parameters
 * @param {string} params.companyName - Company name
 * @param {string} params.companyIdentifier - Company ticker or CIK
 * @param {string} params.companyRegion - Company region ('us' or 'non-us')
 * @param {Object[]} params.selectedFilings - Selected filings to process
 * @param {Object[]} params.selectedTranscripts - Selected transcripts to process
 * @param {boolean} params.justUpdateKnowledgeBase - Only update knowledge base, skip exposure analysis
 * @param {boolean} params.updateCompanyInfo - Re-run company profile analysis
 * @param {boolean} params.isMinimalRun - Whether to run a minimal run
 * @param {boolean} params.rerunResearchOnly - Skip KB update, only re-run research pipeline
 * @returns {Promise} Promise with task information
 */
const processDocuments = async ({ companyName, companyIdentifier, companyRegion, selectedFilings, selectedTranscripts, justUpdateKnowledgeBase = false, updateCompanyInfo = false, isMinimalRun = false, rerunResearchOnly = false, searchResultsDocuments, exchange = null, exchangeSlug = null, skipVerification = false, uen = null, sgxCode = null, portfolioId = null }) => {
  const requestData = {
    company_name: companyName,
    company_identifier: companyIdentifier,
    company_region: companyRegion,
    selected_filings: selectedFilings,
    selected_transcripts: selectedTranscripts,
    just_update_knowledge_base: justUpdateKnowledgeBase,
    update_company_info: updateCompanyInfo,
    is_minimal_run: isMinimalRun,
    rerun_research_only: rerunResearchOnly
  };

  // Add search results documents if provided (for non-US companies)
  if (searchResultsDocuments) {
    requestData.search_results_documents = searchResultsDocuments;
  }
  if (exchange) requestData.exchange = exchange;
  if (exchangeSlug) requestData.exchange_slug = exchangeSlug;
  if (skipVerification) requestData.skip_verification = true;
  if (uen) requestData.uen = uen;
  if (sgxCode) requestData.sgx_code = sgxCode;
  if (portfolioId) requestData.portfolio_id = portfolioId;

  return axios
    .post('onboarding/process-documents', requestData)
    .then((response) => ({
      success: response.success,
      message: response.message,
      taskId: response.task_id
    }))
    .catch((error) => {
      console.error('Process documents error:', error);
      throw error;
    });
};

/**
 * Process manually uploaded documents
 * @param {Object} params - The process parameters
 * @param {string} params.companyName - Company name
 * @param {string} params.companyIdentifier - Company ticker or CIK
 * @param {string} params.companyRegion - Company region ('us' or 'non-us')
 * @param {string} params.fileType - File type (e.g., "10-K", "10-Q")
 * @param {string} params.reportDate - Report year (e.g., "2025")
 * @param {string} params.quarter - Quarter (e.g., "Q1", "Q2")
 * @param {boolean} params.isPresentation - Whether files are presentations
 * @param {File[]} params.files - Array of File objects to upload
 * @param {boolean} params.justUpdateKnowledgeBase - Only update knowledge base, skip exposure analysis
 * @param {boolean} params.updateCompanyInfo - Re-run company profile analysis
 * @param {boolean} params.isMinimalRun - Whether to run a minimal run
 * @returns {Promise} Promise with task information
 */
const processManualDocuments = async ({
  companyName,
  companyIdentifier,
  companyRegion,
  fileType,
  reportDate,
  quarter,
  isPresentation,
  files,
  justUpdateKnowledgeBase = false,
  updateCompanyInfo = false,
  isMinimalRun = false,
  exchange = null,
  exchangeSlug = null,
  portfolioId = null
}) => {
  // Create FormData to send files
  const formData = new FormData();

  // Append files
  files.forEach((file) => {
    formData.append('files', file);
  });

  // Append other fields
  formData.append('company_name', companyName);
  formData.append('company_identifier', companyIdentifier || '');
  formData.append('company_region', companyRegion || '');
  formData.append('file_type', fileType);
  formData.append('report_date', reportDate);
  formData.append('quarter', quarter);
  formData.append('is_presentation', isPresentation);
  formData.append('just_update_knowledge_base', justUpdateKnowledgeBase);
  formData.append('update_company_info', updateCompanyInfo);
  formData.append('is_minimal_run', isMinimalRun);
  if (exchange) formData.append('exchange', exchange);
  if (exchangeSlug) formData.append('exchange_slug', exchangeSlug);
  if (portfolioId) formData.append('portfolio_id', portfolioId);

  return axios
    .post('onboarding/process-manual-documents', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
    .then((response) => ({
      success: response.success,
      message: response.message,
      taskId: response.task_id
    }))
    .catch((error) => {
      console.error('Process manual documents error:', error);
      throw error;
    });
};

/**
 * Get status of a submitted task
 * @param {number} taskId - The task ID to check
 * @returns {Promise} Promise with task status
 */
const getTaskStatus = async (taskId) => {
  return axios
    .get(`onboarding/task-status/${taskId}`)
    .then((response) => ({
      success: response.success,
      taskId: response.task_id,
      filename: response.filename,
      status: response.status,
      message: response.message,
      progress: response.progress,
      submittedDate: response.submitted_date,
      formTypes: response.form_types,
      reportDate: response.report_date,
      quarter: response.quarter,
      companyName: response.company_name,
      isManual: response.is_manual,
      isPresentation: response.is_presentation
    }))
    .catch((error) => {
      console.error('Get task status error:', error);
      throw error;
    });
};

/**
 * Get recent submitted tasks
 * @param {number} limit - Maximum number of tasks to return (default: 10)
 * @returns {Promise} Promise with recent tasks
 */
const getRecentTasks = async (limit = 10) => {
  return axios
    .get(`onboarding/recent-tasks?limit=${limit}`)
    .then((response) => ({
      success: response.success,
      tasks: (response.tasks || []).map(task => {
        // Helper function to safely parse JSON fields
        const parseJsonField = (field) => {
          try {
            if (typeof field === 'string') {
              return JSON.parse(field);
            }
            return field;
          } catch {
            return [];
          }
        };

        // Parse JSON fields
        const companyNames = parseJsonField(task.company_name);
        const formTypes = parseJsonField(task.form_types);
        const reportDates = parseJsonField(task.report_date);
        const quarters = parseJsonField(task.quarter);

        // Format company name
        const companyName = Array.isArray(companyNames) && companyNames.length > 0
          ? [...new Set(companyNames)]
              .map(name => name.replace(/_/g, ' '))
              .join(', ')
          : task.company_name || 'Unknown';

        // Format form types
        const formTypesStr = Array.isArray(formTypes) && formTypes.length > 0
          ? [...new Set(formTypes)].join(', ')
          : task.form_types || '';

        // Format date range
        let dateRange = '';
        if (Array.isArray(reportDates) && reportDates.length > 0) {
          const years = reportDates
            .filter(d => d && d !== '')
            .map(d => parseInt(d))
            .filter(y => !isNaN(y));
          
          if (years.length > 0) {
            const minYear = Math.min(...years);
            const maxYear = Math.max(...years);
            dateRange = minYear === maxYear ? `${minYear}` : `${minYear}-${maxYear}`;
          }
        } else {
          dateRange = task.report_date || '';
        }

        // Format quarters (only if not empty)
        let quartersStr = '';
        if (Array.isArray(quarters) && quarters.length > 0) {
          const cleanQuarters = quarters
            .filter(q => q && q !== '')
            .map(q => {
              // Remove trailing dashes and year info (e.g., "Q32024-" -> "Q3")
              let cleaned = q.replace(/-$/, '').replace(/\d{4}-?$/, '');
              // If it looks like "FY2024-", extract just "FY"
              if (cleaned.startsWith('FY')) {
                cleaned = 'FY';
              }
              return cleaned;
            })
            .filter(q => q !== '');
          
          if (cleanQuarters.length > 0) {
            const uniqueQuarters = [...new Set(cleanQuarters)];
            quartersStr = uniqueQuarters.join(', ');
          }
        }

        return {
          taskId: task.task_id,
          filename: task.filename,
          status: task.status,
          message: task.message,
          progress: task.progress,
          submittedDate: task.submitted_date,
          companyName,
          formTypes: formTypesStr,
          dateRange,
          quarters: quartersStr,
          isManual: task.is_manual,
          isPresentation: task.is_presentation
        };
      })
    }))
    .catch((error) => {
      console.error('Get recent tasks error:', error);
      throw error;
    });
};

/**
 * Get detailed onboarding log with all items
 * @param {number} onboardingId - The onboarding log ID
 * @returns {Promise} Promise with onboarding log and items
 */
const getOnboardingLogDetail = async (onboardingId) => {
  return axios
    .get(`onboarding/log/${onboardingId}`)
    .then((response) => ({
      success: response.success,
      log: response.log,
      companyId: response.company_id || null,
      third_party_research: response.third_party_research || null,
      items: response.items || [],
      // Live execution state (pipeline_jobs) — the log status alone only
      // knows queued/terminal, not whether the run is actually executing
      runStatus: response.run_status || null,
      job: response.job || null,
      lastActivityDate: response.last_activity_date || null,
      isFetchLog: response.is_fetch_log || false,
      relatedLogId: response.related_log_id || null
    }))
    .catch((error) => {
      console.error('Get onboarding log detail error:', error);
      throw error;
    });
};

/**
 * Get list of recent onboarding logs
 * @param {number} limit - Maximum number of logs to return (default: 20)
 * @returns {Promise} Promise with list of onboarding logs
 */
// `status` filters server-side so the progress pill can find the user's
// in-flight run after a reload — the same move `useClientPackJob.resume()`
// makes with `listSessions({ status: 'generating', limit: 1 })`.
const getOnboardingLogs = async (limit = 20, status = null) => {
  const q = status ? `&status=${encodeURIComponent(status)}` : ''
  return axios
    .get(`onboarding/logs?limit=${limit}${q}`)
    .then((response) => ({
      success: response.success,
      logs: response.logs || []
    }))
    .catch((error) => {
      console.error('Get onboarding logs error:', error);
      throw error;
    });
};

/**
 * Get company identifier by company name from the most recent onboarding log
 * @param {string} companyName - The name of the company to look up
 * @returns {Promise} Promise with company identifier
 */
const getCompanyIdentifier = async (companyName) => {
  return axios
    .get(`onboarding/company-identifier/${encodeURIComponent(companyName)}`)
    .then((response) => ({
      success: response.success,
      companyName: response.company_name,
      companyIdentifier: response.company_identifier,
      companyRegion: response.company_region,
      companyNumber: response.company_number || null
    }))
    .catch((error) => {
      console.error('Get company identifier error:', error);
      throw error;
    });
};

/**
 * Check if there's incomplete onboarding for a company (checkpoint status)
 * @param {string} companyName - The name of the company to check
 * @returns {Promise} Promise with checkpoint status
 */
const getCheckpointStatus = async (companyName) => {
  return axios
    .post('onboarding/checkpoint-status', { company_name: companyName })
    .then((response) => ({
      success: response.success,
      hasCheckpoint: response.has_checkpoint,
      exposureStatus: response.exposure_status || null,
      onboardingId: response.onboarding_id,
      lastUpdated: response.last_updated,
      companyName: response.company_name
    }))
    .catch((error) => {
      console.error('Get checkpoint status error:', error);
      throw error;
    });
};

/**
 * Resume onboarding from checkpoint for a company
 * @param {string} companyName - The name of the company to resume
 * @returns {Promise} Promise with new task information
 */
const resumeOnboarding = async (companyName) => {
  return axios
    .post('onboarding/resume', { company_name: companyName })
    .then((response) => ({
      success: response.success,
      taskId: response.task_id,
      message: response.message
    }))
    .catch((error) => {
      console.error('Resume onboarding error:', error);
      throw error;
    });
};

/**
 * Close an onboarding run out: cancel its pipeline job and stamp the log terminal.
 *
 * Deliberately NOT `pipeline/jobs/{id}/cancel`. That one short-circuits on a job
 * that is already terminal and never touches onboarding_log, so for an abandoned
 * run — whose job a zombie sweep has usually already marked failed — it writes
 * nothing and the log stays 'running' forever.
 *
 * @param {number} logId - The onboarding log ID
 * @returns {Promise} Promise with the cancellation outcome
 */
const cancelOnboardingLog = async (logId) => {
  return axios
    .post(`onboarding/log/${logId}/cancel`)
    .then((response) => ({
      success: response.success,
      message: response.message,
      jobCancelled: response.job_cancelled,
      logClosed: response.log_closed
    }))
    .catch((error) => {
      console.error('Cancel onboarding error:', error);
      throw error;
    });
};

/**
 * Process automatic documents + supplementary manual PDFs in one pass.
 * All documents are chunked into the KB together, then the pipeline runs once.
 */
const processDocumentsCombined = async ({
  companyName,
  companyIdentifier,
  companyRegion,
  selectedFilings,
  selectedTranscripts,
  justUpdateKnowledgeBase = false,
  updateCompanyInfo = false,
  isMinimalRun = false,
  supplementaryFiles = [],
  supplementaryFileType = '10-K',
  supplementaryReportDate = '2025',
  supplementaryQuarter = 'Q1',
  exchange = null,
  exchangeSlug = null,
  uen = null,
  sgxCode = null,
  portfolioId = null
}) => {
  const formData = new FormData();

  // JSON payload for automatic docs
  const requestJson = {
    company_name: companyName,
    company_identifier: companyIdentifier,
    company_region: companyRegion,
    selected_filings: selectedFilings,
    selected_transcripts: selectedTranscripts,
    just_update_knowledge_base: justUpdateKnowledgeBase,
    update_company_info: updateCompanyInfo,
    is_minimal_run: isMinimalRun
  };
  if (exchange) requestJson.exchange = exchange;
  if (exchangeSlug) requestJson.exchange_slug = exchangeSlug;
  if (uen) requestJson.uen = uen;
  if (sgxCode) requestJson.sgx_code = sgxCode;
  if (portfolioId) requestJson.portfolio_id = portfolioId;

  formData.append('request_json', JSON.stringify(requestJson));
  formData.append('supplementary_file_type', supplementaryFileType);
  formData.append('supplementary_report_date', supplementaryReportDate);
  formData.append('supplementary_quarter', supplementaryQuarter);

  supplementaryFiles.forEach(file => {
    formData.append('supplementary_files', file);
  });

  return axios
    .post('onboarding/process-documents-combined', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    .then((response) => ({
      success: response.success,
      message: response.message,
      taskId: response.task_id
    }))
    .catch((error) => {
      console.error('Process combined documents error:', error);
      throw error;
    });
};

/**
 * Search Companies House for UK companies
 * @param {string} query - Search query
 * @param {number} limit - Maximum results (default: 10)
 * @returns {Promise} Promise with search results
 */
const searchCompaniesHouse = async (query, limit = 10) => {
  return axios
    .get(`onboarding/search-companies-house?q=${encodeURIComponent(query)}&limit=${limit}`)
    .then((response) => ({
      success: response.success !== false,
      results: response.results || response.items || []
    }))
    .catch((error) => {
      console.error('Companies House search error:', error);
      throw error;
    });
};

/**
 * Poll fetch-documents task status (async mode for UK/CH flow)
 * @param {number} taskId - The task ID returned by fetchDocuments in async mode
 * @returns {Promise} Promise with current status, progress steps, and partial/final results
 */
const getFetchStatus = async (taskId) => {
  return axios
    .get(`onboarding/fetch-status/${taskId}`)
    .then((response) => ({
      taskId: response.task_id,
      status: response.status,
      progress: response.progress || [],
      filings: response.filings || [],
      transcripts: response.transcripts || [],
      totalFilings: response.total_filings || 0,
      totalTranscripts: response.total_transcripts || 0,
      unprocessedFilings: response.unprocessed_filings || 0,
      unprocessedTranscripts: response.unprocessed_transcripts || 0,
      processedFilings: response.processed_filings || [],
      processedTranscripts: response.processed_transcripts || [],
      settingsUsed: response.settings_used || null,
      message: response.message || ''
    }))
    .catch((error) => {
      console.error('Get fetch status error:', error);
      throw error;
    });
};

/**
 * List pending fetches: document searches that completed but were never
 * processed into an onboarding run (for companies that don't exist yet).
 * Powers the Pending-fetches tab on the onboarding page.
 * @param {number} days - Only fetches started within this many days
 * @returns {Promise<{success: boolean, count: number, pending: Object[]}>}
 */
const listPendingFetches = async (days = 30) => {
  return axios
    .get(`onboarding/pending-fetches?days=${days}`)
    .catch((error) => {
      console.error('List pending fetches error:', error);
      throw error;
    });
};

/**
 * Resume a pending fetch — starts the onboarding pipeline from the saved
 * fetch results (onboarding_log.fetch_results). No new search is run and the
 * document list never round-trips through the browser.
 * @param {number} fetchTaskId - The fetch log id from listPendingFetches
 * @returns {Promise} Promise with task information
 */
const resumePendingFetch = async (fetchTaskId) => {
  return axios
    .post('onboarding/process-documents', { fetch_task_id: fetchTaskId })
    .then((response) => ({
      success: response.success,
      message: response.message,
      taskId: response.task_id
    }))
    .catch((error) => {
      console.error('Resume pending fetch error:', error);
      throw error;
    });
};

// Company-name lookup for the add-company form.
//
// ⚠ NO LONGER `POST /company/lookup`. That endpoint was removed (backend spec 047):
// it was a Perplexity autofind, a SIXTH company resolver with its own ranking rules
// whose answer no other part of the estate could see or agree with, and the form
// then stored whatever it pre-filled. This now calls the canonical resolver — the
// same SEC EDGAR / Companies House / web-agent code the onboarding workspace and
// the chat wizard use — so what the form pre-fills is what the backend would have
// resolved anyway.
//
// ⚠ IT NO LONGER NEEDS A REGION, and that reversal is the point. It used to refuse
// to search without one — "Choose a region first" — while the only region control
// lived in step 2, BELOW the search, so step 1's single action was gated on a
// question asked underneath it. `auto` runs the two registries that answer in
// under a second (EDGAR, Companies House); `non_us` is asked for explicitly,
// because that leg is a web-search agent measured at over 90s from Cloud Run and
// has no business on the path of every search. The region the user never answers
// is read off the candidate they pick.
// ⚠ `venue` NARROWS THE WEB LEG AND IS IGNORED BY THE OTHER TWO. Hong Kong and
// Singapore are not resolvers — the backend folds every unrecognised venue into
// `non_us` — so they are this same web search told where to look. Measured:
// "BYD" alone returns Boyd Gaming Corporation (ticker BYD) beside BYD Company
// Limited; "BYD / Listed in: Hong Kong" returns only the two real ones.
const lookupCompany = async ({ companyName, region = 'auto', venue = null,
                               maxResults = 8, domain = null, exclude = [] }) => {
  return axios
    .post('onboarding/workspace/resolve',
          // ⚠ `domain` SKIPS THE SEARCH. The server resolves a pasted website
          // straight to a candidate — no agent, no wait — because a domain is
          // already a stable identifier (`identity_key` mints `domain:<host>`).
          // `exclude` carries the candidates already rejected, so a second ask
          // looks somewhere new instead of returning the same obvious three.
          { name: companyName, region, venue, limit: maxResults, domain, exclude },
          // ⚠ 240s, and it is NOT a round number picked for comfort. Measured on
          // cloud dev 28 Sep 2026: a `non_us` resolve returned **200 after 152
          // seconds** while this client gave up at 120 — so the search succeeded
          // server-side and the user was told "Lookup failed", which is the worst
          // of both: the work was done, the cost was paid, the answer thrown away.
          // The web leg is an agent running real searches; the two registry legs
          // answer in under two seconds and never come near this ceiling.
          { timeout: 240000 })
    .then((response) => {
      const candidates = (response.candidates || []).map((c) => ({
        companyName: c.display_name ?? '',
        ticker: c.ticker ?? '',
        // The form's region values are 'us' | 'uk' | 'other'; the resolver's are
        // 'us' | 'uk' | 'non_us'.
        region: c.region === 'non_us' ? 'other' : (c.region ?? ''),
        listingCountry: c.status ?? '',
        exchange: (c.extra && c.extra.exchange) || '',
        isPublic: c.ticker ? true : null,
        // The resolver's own score where it has one (EDGAR does); the registry
        // order otherwise. NOT a fabricated 0-100 — the old autofind's match_score
        // came from a language model guessing at its own confidence.
        matchScore: c.score ?? null,
        notes: c.address || c.status || '',
        // Carried through so the caller can confirm this exact entity rather than
        // re-resolving from the name it pre-filled.
        identifier: c.identifier ?? '',
        identifierKind: c.identifier_kind ?? '',
        companyNumber: c.company_number ?? '',
        domain: c.domain ?? '',
        // ⚠ THE CIK WAS NOT MAPPED AT ALL, and it is the strongest key a US
        // candidate carries. EDGAR returns it on every row; dropping it here is
        // how a US company reached `identity_key` with a bare ticker and no
        // exchange, which mints `name:<slug>` — the weak key that cannot tell a
        // holding company from its operating company.
        cik: c.cik ?? '',
        // Where we would fetch this entity's filings from. `fetchFrom` is null
        // when nothing we can read holds them, which is a fact about our reach
        // and is shown as such rather than hidden.
        sourcePlan: c.source_plan || [],
        fetchFrom: c.fetch_from || null,
        // ⚠ THE WHOLE CANDIDATE, VERBATIM, for `ensureCompany` to hand to the
        // writer as a confirmed pick. Everything above is for rendering and for
        // pre-filling the form; this is what keeps the resolver's own answer —
        // CIK, domain, registry legal name, listing country — from being
        // reconstructed by guesswork from one text field.
        raw: c
      }));
      return {
        query: response.query || '',
        // One candidate from a registry IS the unambiguous case.
        exactMatch: candidates.length === 1,
        candidates,
        requiresManualDomain: response.requires_manual_domain || false,
        message: response.message || ''
      };
    })
    .catch((error) => {
      console.error('Company lookup error:', error);
      throw error;
    });
};

// ⚠ `resolveCompany` and `confirmCompany` REMOVED along with the
// /companies/onboard page they served. A company is created in exactly ONE place:
// the add-company route on /onboarding, whose "Lookup Company" button calls
// POST /onboarding/workspace/resolve through `lookupCompany` above.
// POST /onboarding/workspace/confirm no longer exists.

// ── Onboarding Workspace (status + reports; resolve lives above) ─────────────



// Stage 2 detector: onboarding state + identity for a confirmed company (admin-gated).
// Returns stage ('resolved' = no files yet | 'files_loaded') plus the stable identity
// (identifier/region/companyNumber/domain) needed to auto-fetch its documents.
const getCompanyOnboardStatus = async (companyId) => {
  return axios
    .get(`onboarding/workspace/companies/${companyId}/status`)
    .then((response) => ({
      companyId: response.company_id,
      displayName: response.display_name,
      companyIdentifier: response.company_identifier,
      region: response.region,
      companyNumber: response.company_number || null,
      domain: response.domain || null,
      documentCount: response.document_count,
      hasDocuments: response.has_documents,
      stage: response.stage,
      recentlyFetched: response.recently_fetched || false
    }))
    .catch((error) => {
      console.error('Get company onboard status error:', error);
      throw error;
    });
};

// Is there a still-running load for this company? Lets the UI resume showing progress
// after a page reload instead of offering to re-fetch (which would duplicate the job).
const getCompanyActiveLoad = async (companyId) => {
  return axios
    .get(`onboarding/workspace/companies/${companyId}/active-load`)
    .then((response) => ({
      active: response.active,
      logId: response.log_id || null,
      status: response.status || null
    }))
    .catch((error) => {
      console.error('Get active load error:', error);
      throw error;
    });
};

// Stage 3: new-report suite state for a company (drives the 'generate reports' prompt and
// resumes progress when you navigate back while reports are generating).
const getReportsStatus = async (companyId) => {
  return axios
    .get(`onboarding/workspace/companies/${companyId}/reports-status`)
    .then((response) => ({
      expected: response.expected || [],
      generated: response.generated || [],
      generating: response.generating || [],
      pending: response.pending || [],
      total: response.total || 0,
      generatedCount: response.generated_count || 0,
      generatingCount: response.generating_count || 0,
      jobs: response.jobs || []
    }))
    .catch((error) => {
      console.error('Get reports status error:', error);
      throw error;
    });
};

// One-click: dispatch the standard new-report suite (skips reports already running).
const generateReports = async (companyId, { force = false, reportKeys } = {}) => {
  return axios
    .post(`onboarding/workspace/companies/${companyId}/generate-reports${force ? '?force=true' : ''}`, reportKeys ? { report_keys: reportKeys } : undefined)
    .then((response) => ({
      dispatched: response.dispatched || [],
      skippedActive: response.skipped_active || [],
      message: response.message || ''
    }))
    .catch((error) => {
      console.error('Generate reports error:', error);
      throw error;
    });
};

// Stage 3b (optional): T3/T4 third-party source research — enriches the evidence reports
// retrieve from. Reports don't need it; regenerate reports after to incorporate new sources.
const getSourcesStatus = async (companyId) => {
  return axios
    .get(`onboarding/workspace/companies/${companyId}/sources-status`)
    .then((response) => ({
      researched: response.researched,
      researching: response.researching
    }))
    .catch((error) => {
      console.error('Get sources status error:', error);
      throw error;
    });
};

const researchSources = async (companyId) => {
  return axios
    .post(`onboarding/workspace/companies/${companyId}/research-sources`)
    .then((response) => ({
      jobId: response.job_id || null,
      status: response.status,
      message: response.message || ''
    }))
    .catch((error) => {
      console.error('Research sources error:', error);
      throw error;
    });
};

/**
 * The canonical onboarding path — spec 036. Three steps, mirroring the batch
 * orchestrator's own stages, replacing route 4's fetch/process pair.
 *
 * ⚠ THE ORDER IS DIFFERENT AND THAT IS THE POINT. Route 4 fetched FIRST and
 * created the company row at process time, so its search ran on whatever
 * identifiers this form happened to hold. The source plan keys on the
 * identifiers stored on the company ROW, so the row has to exist and be right
 * before discovery, or the plan is built from nothing.
 */

// Step 1 — identity. Idempotent: tops up missing identifiers, never overwrites.
const ensureCompany = async ({
  companyName, companyIdentifier, companyRegion, companyNumber,
  exchange, exchangeSlug, portfolioId, resolved
}) => {
  const payload = { company_name: companyName };
  if (companyIdentifier) payload.company_identifier = companyIdentifier;
  if (companyRegion) payload.company_region = companyRegion;
  if (companyNumber) payload.company_number = companyNumber;
  if (exchange) payload.exchange = exchange;
  if (exchangeSlug) payload.exchange_slug = exchangeSlug;
  if (portfolioId) payload.portfolio_id = portfolioId;
  // ⚠ The candidate the user actually picked. `resolve_and_persist` has always
  // taken a confirmed pick — "wins over hints field by field, and means no lookup
  // is needed" — and nothing on this path ever passed one, so the row was built
  // from whatever survived the form's single identifier box.
  if (resolved) payload.resolved = resolved;

  return axios.post('onboarding/company', payload).then((r) => ({
    success: r.success,
    companyId: r.company_id,
    companyName: r.company_name,
    region: r.region,
    companyIdentifier: r.company_identifier,
    // The profile extraction queued alongside the row. Null when it could not be
    // queued — the wizard then shows the form blank rather than waiting for a job
    // that does not exist. It reads no documents, which is why it runs here and
    // not after discovery: its `domain` and `ir_documents_url` are inputs to the
    // source plan discovery is about to build.
    profileJobId: r.profile_job_id || null
  }));
};

// Step 2 — discovery. Blocks for the scan: a registry query is seconds, an IR
// scrape plus a web sweep can be a minute or two. There is no async/poll mode —
// route 4 had one because its fetch ran in a background thread whose results
// could be stranded (the "pending fetches" tab existed to recover them).
const discoverDocuments = async ({ companyId, fromYear, toYear }, timeoutMs = 300000) => {
  const payload = { company_id: companyId };
  if (fromYear) payload.from_year = fromYear;
  if (toYear) payload.to_year = toYear;

  return axios
    .post('onboarding/discover', payload, { timeout: timeoutMs })
    .then((r) => ({
      success: r.success,
      sessionId: r.session_id,
      companyId: r.company_id,
      totalDocuments: r.total_documents,
      // Already in the shape the selection list renders. Each carries its own
      // `document_id` — selection is BY ID, never by position, because an index
      // is only meaningful against the exact list it came from.
      documents: r.documents || [],
      // A dead registry or a timed-out scrape is a routine outcome. A thin list
      // with a stated reason beats a thin list that looks complete.
      sourceErrors: r.source_errors || []
    }));
};

// Step 2, streamed. Same scan, same payload, but each source is named as it
// starts — NDJSON, one JSON object per line.
//
// ⚠ `fetch`, NOT the axios instance, and that is the only reason this is a
// second function. axios buffers the whole body before it resolves, so a
// progress stream read through it arrives all at once at the end, which is the
// thing being fixed. Auth is therefore assembled here the way api/provider.js
// does it — the bearer token out of localStorage — rather than inherited from
// the interceptor.
//
// `onStep` is advisory: a throw inside it must never fail the scan.
const discoverDocumentsStreaming = async ({ companyId, fromYear, toYear },
                                          onStep, onFound) => {
  const payload = { company_id: companyId };
  if (fromYear) payload.from_year = fromYear;
  if (toYear) payload.to_year = toYear;

  const config = useRuntimeConfig();
  const base = (config.public.apiBaseUrl || '').replace(/\/$/, '');
  const headers = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('token');
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${base}/onboarding/discover/stream`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload)
  });
  // ⚠ FALLS BACK RATHER THAN FAILING. This endpoint ships in a different repo
  // from the page that calls it, so there is always a window where the frontend
  // is newer than the backend it is pointed at — a local server that has not
  // been restarted, or a deploy that has not landed. A 404 here must degrade to
  // the blocking scan, which returns the identical payload, not take discovery
  // down. The reader loses the per-source line; that is all.
  if (res.status === 404 || res.status === 405) {
    return discoverDocuments({ companyId, fromYear, toYear });
  }
  if (!res.ok || !res.body) {
    throw new Error(`Discovery failed (${res.status})`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let result = null;
  let failure = null;

  const consume = (line) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    let msg;
    try {
      msg = JSON.parse(trimmed);
    } catch (e) {
      // A half-written line is not an error; the next chunk completes it.
      return;
    }
    if (msg.event === 'step') {
      try { onStep && onStep(msg.data); } catch (e) { /* advisory */ }
    } else if (msg.event === 'found') {
      // ⚠ ONE LEG'S RESULT, THE MOMENT IT LANDS. The three run in turn — the
      // registry in about two seconds, the site crawl in minutes, the sweep in
      // minutes more — so the registry's answer used to sit unseen for the
      // length of the whole scan. Carries the leg's name, its count, and how
      // many an earlier leg had already found.
      try { onFound && onFound(msg.data); } catch (e) { /* advisory */ }
    } else if (msg.event === 'result') {
      result = msg.data;
    } else if (msg.event === 'error') {
      failure = msg.data;
    }
  };

  // ⚠ A chunk is not a line. Hold the tail until a newline arrives, or a message
  // split across two TCP reads is silently dropped.
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop();
    lines.forEach(consume);
  }
  consume(buffer);

  // ⚠ The status was 200 before the scan even started — a stream commits its
  // headers on the first byte — so a failure arrives in the body and has to be
  // turned back into a throw here, or the caller reads it as an empty result.
  if (failure) throw new Error(failure);
  if (!result) throw new Error('Discovery ended without returning anything.');

  return {
    success: result.success,
    sessionId: result.session_id,
    companyId: result.company_id,
    totalDocuments: result.total_documents,
    documents: result.documents || [],
    sourceErrors: result.source_errors || []
  };
};

// Step 3 — ingest what was chosen, then start the run.
//
// ⚠ An EMPTY documentIds is a legitimate request, not an error: it is how a
// manual-upload onboarding proceeds, where the files came through the staging
// path rather than through discovery.
//
// `regenerateAllReports` is the wizard's tick box. Left off, a company we
// already hold rebuilds only the reports a landing statutory filing justifies;
// on, it rebuilds every one of them.
const ingestDocuments = async ({
  sessionId, documentIds = [], regenerateAllReports = false, isMinimalRun = false
}, timeoutMs = 300000) => {
  return axios
    .post('onboarding/ingest', {
      session_id: sessionId || null,
      document_ids: documentIds,
      regenerate_all_reports: regenerateAllReports,
      is_minimal_run: isMinimalRun
    }, { timeout: timeoutMs })
    .then((r) => ({
      success: r.success,
      taskId: r.task_id,
      documentsSelected: r.documents_selected,
      documentsIngested: r.documents_ingested,
      documentsFailed: r.documents_failed,
      message: r.message
    }));
};

export default {
  listDiscoverySessions: () => axios.get('onboarding/discovery-sessions'),
  cancelDiscoverySession: (sessionId) => axios.post(`onboarding/discovery-sessions/${encodeURIComponent(sessionId)}/cancel`),
  getDiscoverySession: (sessionId) => axios.get(`onboarding/discovery-sessions/${encodeURIComponent(sessionId)}`),
  ensureCompany,
  discoverDocuments,
  discoverDocumentsStreaming,
  ingestDocuments,
  lookupCompany,
  getCompanyOnboardStatus,
  getCompanyActiveLoad,
  getReportsStatus,
  generateReports,
  getSourcesStatus,
  researchSources,
  fetchDocuments,
  processDocuments,
  processDocumentsCombined,
  processManualDocuments,
  getTaskStatus,
  getRecentTasks,
  getOnboardingLogDetail,
  getOnboardingLogs,
  getCompanyIdentifier,
  getCheckpointStatus,
  resumeOnboarding,
  cancelOnboardingLog,
  searchCompaniesHouse,
  getFetchStatus,
  listPendingFetches,
  resumePendingFetch
};
